import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Footer from './Footer.js';
import Chart from './Chart.js';
import { Html } from 'cccisd-wysiwyg';
import { client } from 'cccisd-apollo';
import _uniq from 'lodash/uniq';
import dataQuery from './data.graphql';
import IconUser from './IconUser.js';
import IconChart from './IconChart.js';
import IconBook from './IconBook.js';
import style from './style.css';

const demoData = [
    {
        timepoint: '2025-08-02',
        var1: 1.3,
        var2: 1.9,
        var3: 4.8,
        var4: 1.2,
        var5: 3.5,
    },
    {
        timepoint: '2025-03-18',
        var1: 2.5,
        var2: 2.8,
        var3: 0.4,
        var4: 2.9,
        var5: 1.3,
    },
    {
        timepoint: '2025-04-25',
        var1: 2.8,
        var2: 2.5,
        var3: 2.9,
        var4: 1.3,
        var5: 0.4,
    },
];

const Content = props => {
    const { page, isPreview, student, params, settings, latestTimepointOnly } = props;
    const [data, setData] = useState();

    useEffect(() => {
        if (isPreview) {
            const keys = page.formulas.map((_, index) => `var${index + 1}`);
            const timepoints = settings.previewTimepoints || 2;
            const totalBaseKeys = Object.keys(demoData[0]).length - 1;
            const result = demoData.slice(0, timepoints).map(item => ({
                timepoint: item.timepoint,
                ...keys.reduce((obj, key) => {
                    const index = ((Number(key.slice(3)) - 1) % totalBaseKeys) + 1;
                    const multiplier = page.max ? page.max / 5 : 1;
                    const value = item[`var${index}`];
                    obj[key] = typeof value === 'number' ? Math.round(value * multiplier * 10) / 10 : value;
                    return obj;
                }, {}),
            }));
            setData(result);
            return;
        }

        (async () => {
            const variables = _uniq(page.formulas.map(item => item.variable?.variableId)).filter(Boolean);

            const res = await client.query({
                query: dataQuery,
                variables: {
                    pawnId: params.pawnId,
                    surveyId: settings.surveyId,
                    deploymentId: params.deploymentIds,
                    variables,
                },
                fetchPolicy: 'network-only',
            });

            const result = res.data.roles.anyRole.assignmentProgressList
                .filter(item => Object.keys(item.variablesById).length > 0)
                .map(item => {
                    const vars = page.formulas
                        .filter(formula => formula.variable?.variableId)
                        .reduce((obj, formula, index) => {
                            obj[`var${index + 1}`] = item.variablesById[formula.variable.variableId];
                            return obj;
                        }, {});
                    return { timepoint: item.completedAt, ...vars };
                });

            if (latestTimepointOnly && result.length > 0) {
                const latestTimepoint = result[result.length - 1];
                setData([latestTimepoint]);
            } else {
                setData(result);
            }
        })();
    }, [isPreview, settings, params?.pawnId, params?.deploymentIds]);

    const renderConditionalBlocks = () => {
        if (!data || data.length === 0) {
            return null;
        }
        if (!page.explanations || page.explanations.length === 0) {
            return null;
        }

        const blocks = data
            .map(item => {
                const textVariables = [];
                const varValues = page.formulas
                    .filter(formula => formula.variable?.variableId)
                    .reduce((obj, formula, index) => {
                        obj[formula.variable.variableId] = item[`var${index + 1}`];

                        if (item[`var${index + 1}`]) {
                            textVariables.push({ name: `%${formula.variable.label}%`, value: item[`var${index + 1}`] });
                        }

                        return obj;
                    }, {});

                return {
                    ...item,
                    blocks: page.explanations
                        .filter(item1 => {
                            if (!item1.text) {
                                return false;
                            }
                            if (!item1.conditionVariable) {
                                return true;
                            }

                            let value = varValues[item1.conditionVariable];

                            // check for the string containing the number
                            if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
                                value = Number(value);
                            }

                            if (typeof value !== 'number') {
                                return true;
                            }

                            if (
                                typeof item1.conditionMinValue === 'number' &&
                                typeof item1.conditionMaxValue === 'number' &&
                                item1.conditionMinValue === item1.conditionMaxValue
                            ) {
                                return value === item1.conditionMinValue;
                            }

                            if (typeof item1.conditionMinValue === 'number' && value < item1.conditionMinValue) {
                                return false;
                            }
                            if (typeof item1.conditionMaxValue === 'number' && value >= item1.conditionMaxValue) {
                                return false;
                            }

                            return true;
                        })
                        .map(block => {
                            let text = block.text;
                            if (student.name) {
                                text = text.replace(/%studentName%/gi, student.name);
                            }
                            if (isPreview) {
                                text = text.replace(/%reporterName%/gi, 'Mom');
                            } else if (params?.reporterName) {
                                text = text.replace(/%reporterName%/gi, params.reporterName);
                            }
                            for (const variable of textVariables) {
                                text = text.replaceAll(variable.name, variable.value);
                            }
                            return { id: block.id, text };
                        }),
                };
            })
            .filter(item => item.blocks.length > 0);

        if (blocks.length === 0) {
            return null;
        }

        return (
            <div className={'mb-4 ' + style.block}>
                <div className={style.title}>
                    <IconChart />
                    Scores
                </div>
                <div className="d-flex flex-column gap-3">
                    {blocks.map((item, index) => (
                        <div key={`${item.timepoint}_${index}`}>
                            {data.length > 1 && (
                                <div className="fw-bold text-primary">For assessment taken on {item.timepoint}:</div>
                            )}
                            <div className="d-grid gap-2 ms-3">
                                {item.blocks.map(block => (
                                    <Html key={block.id} content={block.text} />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div className={style.page}>
            <div className={style.body}>
                <div className={style.titleWrapper}>
                    <div>
                        <IconUser />
                    </div>
                    <div>
                        <h3>{page.title}</h3>
                        {page.subtitle ? <h4 className="mt-n1 mb-0">{page.subtitle}</h4> : null}
                    </div>
                </div>
                <div className="mb-1 fst-italic">
                    Completed by <b>{isPreview ? 'Mom' : params?.reporterName}</b>
                </div>
                {page.textAbove ? <Html content={page.textAbove} /> : null}
                <div className="mt-5 mb-5">
                    <Chart page={page} isPreview={isPreview} settings={settings} params={params} data={data} />
                </div>
                {renderConditionalBlocks()}
                {page.textBelow ? (
                    <div className={style.block}>
                        <div className={style.title}>
                            <IconBook />
                            Overview
                        </div>
                        <Html content={page.textBelow} />
                    </div>
                ) : null}
            </div>
            <Footer />
        </div>
    );
};

Content.propTypes = {
    page: PropTypes.object,
    isPreview: PropTypes.bool,
    student: PropTypes.object,
    params: PropTypes.object,
    settings: PropTypes.object,
    latestTimepointOnly: PropTypes.bool,
};

export default Content;
